import { immerable } from '@utils';
import { Elements } from './Elements';
import { Graph } from './Graph';
import { canMoveDAGEdge, isTree, topologicalSort } from './algo';
import type { MutableTree } from './interface/MutableTree';
import type { EID, EdgeDirection, Edges, NID, Nodes } from './types';

export type BlankTree = Tree<unknown, unknown, unknown>;

export class Tree<G, N, E> implements MutableTree<G, N, E> {
  [immerable] = true;

  private _root: NID;
  private _inner: Graph<G, N, E>;

  constructor(opts?: { graph: Graph<G, N, E> } | { data?: G; root?: NID; rootData?: N }) {
    if (opts !== undefined && 'graph' in opts) {
      const graph = opts.graph;
      const roots = graph.roots;
      if (roots.size !== 1) {
        throw new Error('Not a tree');
      }
      const root = [...roots][0];
      if (!isTree(graph, root)) {
        throw new Error('Not a tree');
      }
      this._root = root;
      this._inner = graph;
    } else {
      this._root = opts?.root ?? 'root';
      this._inner = new Graph<G, N, E>(opts).addNode(this._root, {
        data: opts?.rootData
      });
    }
  }

  // Get the graph of the tree.
  //
  // Note: This is for read-only traversal and analysis purposes. Do not mutate
  // the returned object.
  get graph(): Graph<G, N, E> {
    return this._inner;
  }

  //
  // VisitableData
  //

  get data(): G {
    return this._inner.data;
  }

  nodeData(id: NID): N {
    return this._inner.nodeData(id);
  }

  edgeData(id: EID): E {
    return this._inner.edgeData(id);
  }

  //
  // MutableData
  //

  setData(data: G): this {
    this._inner.setData(data);
    return this;
  }

  setNodeData(id: NID, data: N): this {
    this._inner.setNodeData(id, data);
    return this;
  }

  setEdgeData(id: EID, data: E): this {
    this._inner.setEdgeData(id, data);
    return this;
  }

  withData(f: (data: G) => void): this {
    this._inner.withData(f);
    return this;
  }

  withNodeData(id: NID, f: (data: N) => void): this {
    this._inner.withNodeData(id, f);
    return this;
  }

  withEdgeData(id: EID, f: (data: E) => void): this {
    this._inner.withEdgeData(id, f);
    return this;
  }

  //
  // VisitableTree
  //

  get root(): NID {
    return this._root;
  }

  //
  // VisitableGraph
  //

  get isEmpty(): boolean {
    return this._inner.isEmpty;
  }

  get nodeCount(): number {
    return this._inner.nodeCount;
  }

  get edgeCount(): number {
    return this._inner.edgeCount;
  }

  get nodes(): Nodes {
    return this._inner.nodes;
  }

  get edges(): Edges {
    return this._inner.edges;
  }

  get elements(): Elements {
    return this._inner.elements;
  }

  hasNode(id: NID): boolean {
    return this._inner.hasNode(id);
  }

  hasEdge(id: EID): boolean {
    return this._inner.hasEdge(id);
  }

  hasEdgeFromTo(source: NID, target: NID): boolean {
    return this._inner.hasEdgeFromTo(source, target);
  }

  hasEdgeBetween(a: NID, b: NID): boolean {
    return this._inner.hasEdgeBetween(a, b);
  }

  source(id: EID): NID {
    return this._inner.source(id);
  }

  target(id: EID): NID {
    return this._inner.target(id);
  }

  endpoints(id: EID): [NID, NID] {
    return this._inner.endpoints(id);
  }

  endpoint(id: EID, direction: EdgeDirection): NID {
    return this._inner.endpoint(id, direction);
  }

  outEdges(id: NID): Edges {
    return this._inner.outEdges(id);
  }

  inEdges(id: NID): Edges {
    return this._inner.inEdges(id);
  }

  incidentEdges(id: NID): Edges {
    return this._inner.incidentEdges(id);
  }

  outDegree(id: NID): number {
    return this._inner.outDegree(id);
  }

  inDegree(id: NID): number {
    return this._inner.inDegree(id);
  }

  degree(id: NID): number {
    return this._inner.degree(id);
  }

  successors(id: Nodes | NID): Nodes {
    return this._inner.successors(id);
  }

  transitiveSuccessors(id: Nodes | NID): Nodes {
    return this._inner.transitiveSuccessors(id);
  }

  predecessors(id: Nodes | NID): Nodes {
    return this._inner.predecessors(id);
  }

  transitivePredecessors(id: Nodes | NID): Nodes {
    return this._inner.transitivePredecessors(id);
  }

  neighbors(id: NID): Nodes {
    return this._inner.neighbors(id);
  }

  hasSuccessors(id: NID): boolean {
    return this._inner.hasSuccessors(id);
  }

  hasPredecessors(id: NID): boolean {
    return this._inner.hasPredecessors(id);
  }

  hasNeighbors(id: NID): boolean {
    return this._inner.hasNeighbors(id);
  }

  get roots(): Nodes {
    return this._inner.roots;
  }

  get leaves(): Nodes {
    return this._inner.leaves;
  }

  get nonRoots(): Nodes {
    return this._inner.nonRoots;
  }

  get nonLeaves(): Nodes {
    return this._inner.nonLeaves;
  }

  get internals(): Nodes {
    return this._inner.internals;
  }

  isRoot(id: NID): boolean {
    return this._inner.isRoot(id);
  }

  isLeaf(id: NID): boolean {
    return this._inner.isLeaf(id);
  }

  isInternal(id: NID): boolean {
    return this._inner.isInternal(id);
  }

  //
  // VisitableTreeish
  //

  inEdge(node: NID): EID | undefined {
    const inEdges = this.inEdges(node);
    if (inEdges.size === 0) {
      return undefined;
    }
    return inEdges.values().next().value;
  }

  parent(node: NID): NID | undefined {
    const inEdge = this.inEdge(node);
    if (inEdge === undefined) {
      return undefined;
    }
    return this.source(inEdge);
  }

  hasParent(node: NID): boolean {
    return this.inEdge(node) !== undefined;
  }

  children(node: NID): Nodes {
    return this.successors(node);
  }

  hasChildren(node: NID): boolean {
    return this.hasSuccessors(node);
  }

  childCount(node: NID): number {
    return this.outDegree(node);
  }

  descendants(node: NID): Nodes {
    return this.transitiveSuccessors(node);
  }

  hasDescendant(node: NID, descendant: NID): boolean {
    return this.descendants(node).has(descendant);
  }

  ancestors(node: NID): Nodes {
    return this.transitivePredecessors(node);
  }

  hasAncestor(node: NID, ancestor: NID): boolean {
    return this.ancestors(node).has(ancestor);
  }

  pathToRoot(node: NID): NID[] {
    const path = [];
    let current: NID | undefined = node;
    while (current !== undefined) {
      path.push(current);
      current = this.parent(current);
    }
    return path;
  }

  depth(node: NID): number {
    const path = this.pathToRoot(node);
    return path.length - 1;
  }

  //
  // MutableTree
  //

  addChild(id: NID, edge: EID, opts?: { data?: N; parent?: NID; edgeData?: E }): this {
    const parentID = opts?.parent ?? this.root;
    this._inner.addNode(id, opts);
    this._inner.addEdge(edge, parentID, id, opts?.edgeData);
    return this;
  }

  //
  // MutableTreeBase
  //

  removeNodeUngrouping(id: NID): this {
    const children = this.children(id);

    if (id === this.root) {
      // Can only remove root node if it has a single child to promote to root.
      if (children.size > 1) {
        throw new Error('Cannot remove the root node with multiple children');
      }
      this._inner.removeNode(id);
      const nextChild = children.values().next().value;
      if (nextChild === undefined) {
        throw new Error('Root node has no children to promote');
      }
      this._root = nextChild;
    } else {
      const newParent = this.parent(id);
      if (newParent === undefined) {
        throw new Error('Parent node not found');
      }
      for (const child of children) {
        this.moveNode(child, newParent);
      }
      this._inner.removeNode(id);
    }
    return this;
  }

  removeNodeAndDescendants(id: NID): Nodes {
    if (id === this.root) {
      throw new Error('Cannot remove the root node');
    }
    return this._removeDescendants(new Set([id]));
  }

  removeDescendants(id: NID): Nodes {
    return this._removeDescendants(this.children(id));
  }

  private _removeDescendants(children: Nodes) {
    // Remove child nodes in reverse-topological sort order (most distant from the target first).
    const toRemove = topologicalSort(this, {
      roots: children,
      rootsOnly: true
    }).reverse();
    for (const node of toRemove) {
      this._inner.removeNode(node);
    }

    return new Set(toRemove);
  }

  moveNode(id: NID, newParent: NID): this {
    if (id === this.root) {
      throw new Error('Cannot move the root node');
    }
    const edge = this.inEdge(id);
    if (edge === undefined) {
      throw new Error('Node has no parent');
    }
    if (!canMoveDAGEdge(this, edge, { newSource: newParent })) {
      throw new Error('Cycle detected');
    }
    this._inner.moveEdge(edge, { newSource: newParent });

    return this;
  }

  /**
   * Finds the least common ancestor (LCA) of a subset of nodes in the tree.
   *
   * @param nodes - A subset of node IDs for which to find the LCA.
   * @returns The node ID of the least common ancestor.
   * @throws If the nodes set is empty or if any node does not exist in the tree.
   */
  leastCommonAncestor(nodes: Nodes): NID {
    // Ensure that the subset is not empty
    if (nodes.size === 0) {
      throw new Error('At least one node is required to find the least common ancestor.');
    }

    // Convert Nodes set to an array for easier handling
    const nodeArray = Array.from(nodes);

    // Check if all nodes exist in the tree
    for (const node of nodeArray) {
      if (!this.hasNode(node)) {
        throw new Error(`Node '${node}' does not exist in the tree.`);
      }
    }

    // If only one node is provided, return the node itself
    if (nodes.size === 1) {
      return nodeArray[0];
    }

    // Collect the path from each node to the root and reverse it to start from the root
    const paths: NID[][] = nodeArray.map(node => this.pathToRoot(node).reverse());

    // Find the minimum length among all paths
    const minLength = Math.min(...paths.map(path => path.length));
    let lca: NID = this.root; // Initialize LCA as root

    // Iterate over the paths to find the deepest common node
    for (let i = 0; i < minLength; i++) {
      const currentNodes = new Set(paths.map(path => path[i]));
      if (currentNodes.size === 1) {
        // All nodes at this level are the same; update LCA
        lca = paths[0][i];
      } else {
        // Nodes diverge at this level; stop searching
        break;
      }
    }

    return lca;
  }
}
